/**
 * referralRequired — s106 (app referral screens, canvas "Orella Referrals Screens" A2; referrals plan
 * decisions 2 and 9). Does THIS specialist need a referral before THIS patient can book?
 *
 * ★★ THE TWIN OF THE API'S _referral_required (orella-api/api/index.py, with _ref_match_provider,
 *   _ref_plan_required, _ref_default_required, _ref_norm, _ins_plan_type). The server is the authority: it
 *   locks booking (_referral_lock_refusal) whatever the app shows. The app asks the same question
 *   BEFORE the case is sent, so the patient can choose (A2). scripts/check-referral-screens.mjs runs this
 *   file against the API's own vectors; change both or neither.
 *
 * accepted: the specialist's accepted_insurance (object, or the legacy list); ins: profile.insurance.
 * Pure, no imports.
 */
const clean = (v) => String(v == null ? '' : v).trim()
const norm = (v) => String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9]/g, '')
// The API's _json_bool: a boolean as is, null -> the default, a string is false only when it is one of
// Postgres's false spellings (or empty), anything else by truthiness.
const jsonBool = (v, dflt) => {
  if (v === true || v === false) return v
  if (v === null || v === undefined) return dflt
  if (typeof v === 'string') return !['', 'f', 'false', '0', 'n', 'no', 'off'].includes(v.trim().toLowerCase())
  return !!v
}

export function insPlanType(v) {
  const s = clean(v)
  return norm(s) === 'selfpay' ? '' : s
}

export function defaultRequired(planType) {
  const n = norm(planType)
  return !!n && (n.includes('hmo') || n.includes('managedcare'))
}

export function matchProvider(providers, ins) {
  const i = ins && typeof ins === 'object' ? ins : {}
  const ids = new Set(['stediPayerId', 'payerId', 'primaryPayerId', 'providerId'].map((k) => clean(i[k])).filter(Boolean))
  const cname = clean(i.provider).toLowerCase()
  for (const p of Array.isArray(providers) ? providers : []) {
    if (!p || typeof p !== 'object') continue
    if ([clean(p.payerId), clean(p.id)].filter(Boolean).some((x) => ids.has(x))) return p
    const n = clean(p.name).toLowerCase()
    if (n && cname && (n.includes(cname) || cname.includes(n))) return p
  }
  return null
}

export function planRequired(provider, planType) {
  const flags = provider && typeof provider === 'object' ? provider.referralRequired : null
  if (flags && typeof flags === 'object' && !Array.isArray(flags)) {
    for (const [k, v] of Object.entries(flags)) if (norm(k) === norm(planType)) return !!v
    if ('*' in flags) return !!flags['*']
  }
  return defaultRequired(planType)
}

/** True when the specialist needs a referral before this patient can book. */
export function referralRequired(accepted, ins) {
  let acc = accepted
  if (typeof acc === 'string') { try { acc = JSON.parse(acc) } catch (_) { acc = {} } }
  if (Array.isArray(acc)) acc = { providers: acc }
  acc = acc && typeof acc === 'object' ? acc : {}
  const i = ins && typeof ins === 'object' ? ins : {}
  const prov = matchProvider(acc.providers || [], i)
  const le = i.last_eligibility && typeof i.last_eligibility === 'object' ? i.last_eligibility : {}
  const pt = insPlanType(i.planType) || insPlanType(le.planType) || insPlanType(i.coverageType)
  if (!prov) {
    if (jsonBool(acc.accept_all, false) && jsonBool(acc.referral_hmo_default, true)) return defaultRequired(pt)
    return false
  }
  if (pt) return planRequired(prov, pt)
  const types = (prov.activePlanTypes || prov.planTypes || []).filter((t) => clean(t))
  return types.length > 0 && types.every((t) => planRequired(prov, t))
}

/** The plan's display name for the chips and copy ("Blue Shield of California · HMO"). */
export function planLabel(ins) {
  const i = ins && typeof ins === 'object' ? ins : {}
  const le = i.last_eligibility && typeof i.last_eligibility === 'object' ? i.last_eligibility : {}
  const pt = insPlanType(i.planType) || insPlanType(le.planType)
  return [clean(i.provider), pt].filter(Boolean).join(' · ')
}

/** Is this doctor the patient's own primary care doctor (insurance.pcp)? */
export function isOwnPcp(doc, pcp) {
  if (!doc || !pcp) return false
  if (pcp.doctorId && doc.id && String(pcp.doctorId) === String(doc.id)) return true
  return !!(pcp.npi && doc.npi && String(pcp.npi) === String(doc.npi))
}
